/**
 * Video Thumbnail Extraction Utilities
 *
 * Extracts thumbnail images from video files using HTML5 video and canvas APIs
 */

/**
 * Extract a thumbnail image from a video file
 *
 * @param {File} videoFile - The video file to extract thumbnail from
 * @param {number} seekTime - Time in seconds to capture the thumbnail (default: 1)
 * @returns {Promise<File>} - A File object containing the thumbnail image (JPEG)
 */
export const extractThumbnailFromVideo = (videoFile, seekTime = 1) => {
  return new Promise((resolve, reject) => {
    // Create video element
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    // Create object URL for the video
    const videoURL = URL.createObjectURL(videoFile);
    video.src = videoURL;

    // When metadata is loaded, seek to the desired time
    video.addEventListener('loadedmetadata', () => {
      // Ensure seekTime doesn't exceed video duration
      const targetTime = Math.min(seekTime, video.duration * 0.25); // Use first 25% of video
      video.currentTime = targetTime;
    });

    // When seeked to the time, capture the frame
    video.addEventListener('seeked', () => {
      try {
        // Create canvas with video dimensions
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Draw video frame to canvas
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to create thumbnail blob'));
              return;
            }

            // Create File from blob
            const thumbnailFile = new File(
              [blob],
              videoFile.name.replace(/\.[^/.]+$/, '') + '-thumbnail.jpg',
              { type: 'image/jpeg' }
            );

            // Clean up
            URL.revokeObjectURL(videoURL);
            video.remove();

            resolve(thumbnailFile);
          },
          'image/jpeg',
          0.85
        ); // 85% quality JPEG
      } catch (error) {
        URL.revokeObjectURL(videoURL);
        video.remove();
        reject(error);
      }
    });

    // Handle errors
    video.addEventListener('error', () => {
      URL.revokeObjectURL(videoURL);
      video.remove();
      reject(new Error('Failed to load video for thumbnail extraction'));
    });

    // Start loading the video
    video.load();
  });
};

/**
 * Extract multiple thumbnails from a video file at different time points
 *
 * @param {File} videoFile - The video file to extract thumbnails from
 * @param {number} count - Number of thumbnails to extract (default: 3)
 * @returns {Promise<File[]>} - Array of File objects containing thumbnail images
 */
export const extractMultipleThumbnails = async (videoFile, count = 3) => {
  const video = document.createElement('video');
  video.preload = 'metadata';
  video.muted = true;
  video.playsInline = true;

  const videoURL = URL.createObjectURL(videoFile);
  video.src = videoURL;

  return new Promise((resolve, reject) => {
    video.addEventListener('loadedmetadata', async () => {
      try {
        const thumbnails = [];
        const interval = video.duration / (count + 1);

        for (let i = 1; i <= count; i++) {
          const seekTime = interval * i;

          // Seek to position
          await new Promise((seekResolve) => {
            video.currentTime = seekTime;
            video.addEventListener('seeked', seekResolve, { once: true });
          });

          // Capture frame
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          const blob = await new Promise((blobResolve) => {
            canvas.toBlob(blobResolve, 'image/jpeg', 0.85);
          });

          if (blob) {
            const thumbnailFile = new File(
              [blob],
              `${videoFile.name.replace(/\.[^/.]+$/, '')}-thumbnail-${i}.jpg`,
              { type: 'image/jpeg' }
            );
            thumbnails.push(thumbnailFile);
          }
        }

        URL.revokeObjectURL(videoURL);
        video.remove();
        resolve(thumbnails);
      } catch (error) {
        URL.revokeObjectURL(videoURL);
        video.remove();
        reject(error);
      }
    });

    video.addEventListener('error', () => {
      URL.revokeObjectURL(videoURL);
      video.remove();
      reject(new Error('Failed to load video for thumbnail extraction'));
    });

    video.load();
  });
};

/**
 * Get video duration in seconds
 *
 * @param {File} videoFile - The video file
 * @returns {Promise<number>} - Duration in seconds
 */
export const getVideoDuration = (videoFile) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;

    const videoURL = URL.createObjectURL(videoFile);
    video.src = videoURL;

    video.addEventListener('loadedmetadata', () => {
      const duration = video.duration;
      URL.revokeObjectURL(videoURL);
      video.remove();
      resolve(duration);
    });

    video.addEventListener('error', () => {
      URL.revokeObjectURL(videoURL);
      video.remove();
      reject(new Error('Failed to load video metadata'));
    });

    video.load();
  });
};
